import { Point } from "mapbox-gl";
import { RootStore } from "../../shared/stores/RootStore";
import { nodeReferences } from "../../osaway/stores/OsaNodeStore";
import { osaNodeShiftDragLayerName } from "./OsaMapSource";
import { hlaWayShiftDragLayerName } from "./HlaMapSource";
import { BBoxSelection } from "./BBoxSelection";
import { MapMode } from "../../project/stores/MapStore";
import { mapillaryPointShiftDragLayerName } from "./MapillaryMapSource";

export class ShiftDragSelection {
  map: mapboxgl.Map;
  rootStore: RootStore;
  bboxSelection?: BBoxSelection;

  selectOsaNodes = false;
  selectHlaWays = false;
  selectMapillaryPoints = false;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
  }

  setSelectOsaNodes(value: boolean) {
    this.selectOsaNodes = value;
  }

  setSelectHlaWays(value: boolean) {
    this.selectHlaWays = value;
  }

  onModeChange(mode: MapMode) {
    this.disable();
    if (
      mode === MapMode.ProjectPage ||
      mode === MapMode.ManageNodes ||
      mode === MapMode.MapillaryImport
    ) {
      this.enable();
    }
  }

  setSelectMapillaryPoints(value: boolean) {
    this.selectMapillaryPoints = value;
  }

  disable() {
    this.bboxSelection?.disable();
  }

  enable() {
    this.bboxSelection = new BBoxSelection(this.map, {
      onDrag: (bbox) => this.onFinish(bbox),
      onClick: (point) => this.onFinish(point),
    });
    this.bboxSelection.enable();
  }

  onFinish(query: Point | [Point, Point]) {
    const { osaWayStore, osaNodeStore, hlaStore, mapillaryStore } =
      this.rootStore;

    if (this.selectOsaNodes) {
      const selectedWay = osaWayStore.getSelected();
      if (selectedWay) {
        const features = this.map.queryRenderedFeatures(query, {
          layers: [osaNodeShiftDragLayerName],
        });

        features.forEach((f) => {
          const nodeId = f.properties?.id as string;

          const nodeIsInSelectedWay =
            nodeReferences.get(nodeId)?.osaWayId === selectedWay.id;

          if (nodeIsInSelectedWay) {
            osaNodeStore.toggleMultiSelectedId(nodeId);
          }
        });
      }
    }

    if (this.selectHlaWays) {
      const features = this.map.queryRenderedFeatures(query, {
        layers: [hlaWayShiftDragLayerName],
      });

      const hlaWayIds = new Set(
        features.map((f) => f.properties?.hlaWayId as string),
      );

      hlaWayIds.forEach((hlaWayId) => {
        hlaStore.toggleMultiSelectedId(hlaWayId);
      });
    }

    if (this.selectMapillaryPoints) {
      const selectedSequenceId = mapillaryStore.selectedSequenceId;
      if (!selectedSequenceId) return;

      const features = this.map.queryRenderedFeatures(query, {
        layers: [mapillaryPointShiftDragLayerName],
      });

      const imageIds = new Set<string>();

      features.forEach((f) => {
        const imageId = f.properties?.id as string | undefined;
        const sequenceId = f.properties?.sequenceId as string | undefined;

        if (!imageId) return;
        if (sequenceId !== selectedSequenceId) return;

        imageIds.add(imageId);
      });

      imageIds.forEach((imageId) => {
        mapillaryStore.togglePointSelected(imageId);
      });
    }
  }
}
